import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { useCollectionData } from "react-firebase-hooks/firestore";
import {
  collection,
  collectionGroup,
  doc,
  orderBy,
  query,
  updateDoc,
  where,
  getDoc,
  limit,
  getCountFromServer,
} from "firebase/firestore";
import { firestore } from "@/firebase";
import {
  programFeatureUserConverter,
  taskerConfigConverter,
  taskerMessageConverter,
} from "@/converters";
import { TaskerConfig, TaskerMessage } from "@t3/shared";
import { useAuthContext } from "../../context/AuthContext";
import { useCompanyConfigContext } from "../../context/CompanyConfigContext";
import { useTimeContext } from "../../context/TimeContext";

type TaskerMessageContextType = {
  userTaskerConfigs: TaskerConfig[];
  managerTaskerConfigs: TaskerConfig[];
  taskerMyMessages: TaskerMessage[];
  loading: boolean;
  error: Error | undefined | null;
  loadMoreMessages: () => void;
  taskerAssignedMessages: TaskerMessage[];
  updateTaskerMessage: (
    messageId: string,
    update: Partial<TaskerMessage>,
  ) => Promise<void>;
  hasMoreMessages: boolean;
};

const TaskerMessageContext = createContext<TaskerMessageContextType>({
  userTaskerConfigs: [],
  managerTaskerConfigs: [],
  taskerMyMessages: [],
  loading: false,
  error: null,
  loadMoreMessages: () => {},
  taskerAssignedMessages: [],
  updateTaskerMessage: async () => {},
  hasMoreMessages: false,
});

const useTaskerPrograms = () => {
  const { user } = useAuthContext();
  const programFeatureUsersRef = query(
    collectionGroup(firestore, "program-feature-users").withConverter(
      programFeatureUserConverter,
    ),
    where("userId", "==", user.id),
    where("featureType", "==", "tasker"),
  );

  const programFeatureUsersUserRoleRef = query(
    programFeatureUsersRef,
    where("roles", "array-contains", "user"),
  );

  const programFeatureUsersManagerRoleRef = query(
    programFeatureUsersRef,
    where("roles", "array-contains", "manager"),
  );

  const [programFeatureUsersUserRoleSnapshot] = useCollectionData(
    programFeatureUsersUserRoleRef,
  );

  const [programFeatureUsersManagerRoleSnapshot] = useCollectionData(
    programFeatureUsersManagerRoleRef,
  );

  const userRoleProgramIds = programFeatureUsersUserRoleSnapshot?.map(
    (doc) => doc.programId,
  );

  const managerRoleProgramIds = programFeatureUsersManagerRoleSnapshot?.map(
    (doc) => doc.programId,
  );

  const userRoleTaskerConfigs = useTaskerConfigs(userRoleProgramIds);
  const managerRoleTaskerConfigs = useTaskerConfigs(managerRoleProgramIds);

  return {
    userRoleTaskerConfigs,
    managerRoleTaskerConfigs,
  };
};

const useTaskerConfigs = (programIds?: string[]) => {
  const { company } = useCompanyConfigContext();
  const [results, setResults] = useState<TaskerConfig[]>([]);

  const pIds = (programIds || []).join();

  // Create an array of queries for each program ID
  useEffect(() => {
    if (!programIds?.length || !company?.id) {
      return;
    }
    const snaps = programIds.map((programId) => {
      const configRef = doc(
        firestore,
        `company/${company.id}/programs/${programId}/features/tasker`,
      ).withConverter(taskerConfigConverter);

      return getDoc(configRef).then((snap) => snap.data());
    });
    Promise.all(snaps).then((results) => {
      setResults(results.filter((result) => result !== undefined));
    });
  }, [pIds, company?.id]);

  return results;
};

export const useTaskerMessageContext = () => useContext(TaskerMessageContext);

export const TaskerMessageProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { user } = useAuthContext();
  const { company } = useCompanyConfigContext();
  const { time } = useTimeContext();
  const [updating, setUpdating] = useState(false);
  const [myLimit, setMyLimit] = useState(100);
  const [totalMessageCount, setTotalMessageCount] = useState<number>(0);
  const [taskerMyMessages, setTaskerMyMessages] = useState<TaskerMessage[]>([]);

  const { userRoleTaskerConfigs, managerRoleTaskerConfigs } =
    useTaskerPrograms();

  // Query for tasker messages where the verifiedUser is the current user
  const taskerRef = useMemo(() => {
    return company?.id
      ? collection(firestore, `company/${company.id}/tasker`).withConverter(
          taskerMessageConverter,
        )
      : null;
  }, [company?.id]);

  // Base query without limit
  const taskerMyBaseQuery = useMemo(() => {
    if (!taskerRef || !userRoleTaskerConfigs.length) return null;

    return query(
      taskerRef,
      where("verifiedUser", "==", user.id),
      where(
        "eligibleProgramIds",
        "array-contains-any",
        userRoleTaskerConfigs.map((config) => config.programId),
      ),
      orderBy("timestamp", "desc"),
    );
  }, [taskerRef, user.id, userRoleTaskerConfigs]);

  // Data query with limit
  const taskerMyQuery = useMemo(() => {
    if (!taskerMyBaseQuery) return null;
    return query(taskerMyBaseQuery, limit(myLimit));
  }, [taskerMyBaseQuery, myLimit]);

  const taskerAssignedQuery = useMemo(() => {
    if (!taskerRef || !managerRoleTaskerConfigs.length) return null;

    return query(
      taskerRef,
      where(
        "eligibleProgramIds",
        "array-contains-any",
        managerRoleTaskerConfigs.map((config) => config.programId),
      ),
      where("reviewStatus", "==", "pending"),
      orderBy("timestamp", "asc"),
    );
  }, [taskerRef, managerRoleTaskerConfigs]);

  const [taskerMyMessagesRaw, loadingMy, errorMy] =
    useCollectionData(taskerMyQuery);
  const [taskerAssignedMessages, loadingAssigned, errorAssigned] =
    useCollectionData(taskerAssignedQuery);

  // Fetch total count of messages
  useEffect(() => {
    if (!taskerMyBaseQuery) {
      setTotalMessageCount(0);
      return;
    }

    getCountFromServer(taskerMyBaseQuery)
      .then((snapshot) => {
        setTotalMessageCount(snapshot.data().count);
      })
      .catch((error) => {
        console.error("Error fetching message count:", error);
      });
  }, [taskerMyBaseQuery]);

  // Function to load more pages
  const loadMoreMessages = useCallback(() => {
    setMyLimit((prev) => prev + 100);
  }, []);

  const hasLoadedAllMessages =
    (taskerMyMessagesRaw?.length || 0) >= totalMessageCount;

  // Auto-load messages until we have at least a week
  useEffect(() => {
    if (!taskerMyMessagesRaw?.length || loadingMy) {
      return;
    }

    const oneWeekAgo = time.minus({ weeks: 1 });
    const oldestMessage = taskerMyMessagesRaw[taskerMyMessagesRaw.length - 1];
    const oldestMessageDate = oldestMessage.timestamp;

    const hasAtLeastAWeek = oldestMessageDate <= oneWeekAgo;

    // Auto-load more if we don't have a week yet and haven't loaded all messages
    if (!hasLoadedAllMessages && !hasAtLeastAWeek && !errorMy) {
      loadMoreMessages();
    }
  }, [taskerMyMessagesRaw, loadingMy, myLimit, loadMoreMessages, time]);

  // Trim to only include complete days (exclude last day if incomplete)
  useEffect(() => {
    if (!loadingMy) {
      if (!taskerMyMessagesRaw?.length) {
        console.log("No messages to display");
        setTaskerMyMessages([]);
        return;
      }

      // If we've loaded all messages, return everything
      if (hasLoadedAllMessages) {
        setTaskerMyMessages(taskerMyMessagesRaw);
        return;
      }

      // Otherwise, exclude messages from the oldest day (might be incomplete)
      const oldestMessage = taskerMyMessagesRaw[taskerMyMessagesRaw.length - 1];
      const oldestDay = oldestMessage.timestamp.toISODate();

      setTaskerMyMessages(
        taskerMyMessagesRaw.filter((msg) => {
          const msgDay = msg.timestamp.toISODate();
          return msgDay !== oldestDay;
        }),
      );
    }
  }, [taskerMyMessagesRaw, myLimit]);

  // Function to update a tasker message's review status
  const updateTaskerMessage = useCallback(
    async (messageId: string, update: Partial<TaskerMessage>) => {
      if (!company?.id || !messageId) return;

      try {
        setUpdating(true);
        const messageRef = doc(
          firestore,
          `company/${company.id}/tasker/${messageId}`,
        ).withConverter(taskerMessageConverter);

        await updateDoc(messageRef, {
          ...update,
          init: null,
        });
      } catch (err) {
        console.error("Error updating tasker message status:", err);
        throw err;
      } finally {
        setUpdating(false);
      }
    },
    [company?.id],
  );

  if (errorMy || errorAssigned) {
    console.error("Error fetching tasker messages:", errorMy || errorAssigned);
  }

  // Enhanced loading state
  const overallLoading = loadingMy || loadingAssigned || updating;

  // Check if there are more messages to load by comparing loaded count to total count

  const value: TaskerMessageContextType = {
    userTaskerConfigs: userRoleTaskerConfigs,
    managerTaskerConfigs: managerRoleTaskerConfigs,
    taskerMyMessages,
    loading: overallLoading,
    error: errorMy || errorAssigned,
    loadMoreMessages,
    taskerAssignedMessages: taskerAssignedMessages || [],
    updateTaskerMessage,
    hasMoreMessages: !hasLoadedAllMessages,
  };

  return (
    <TaskerMessageContext.Provider value={value}>
      {children}
    </TaskerMessageContext.Provider>
  );
};
